import { useMemo, useState } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  TextField,
} from '@mui/material'
import { com } from '@eidu/web'
import { useQuery } from '@tanstack/react-query'
import useEntityById from '../../../../io/useEntityById'
import useRegistryAsset from '../../../../io/useRegistryAsset'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import listVersions from '../../../../io/api/entity/data/listVersions'
import { RuntimeRef } from '../../../../io/runtimeRef'
import EntityPickerWithRegistry from './EntityPickerWithRegistry'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString

type RuntimeSelectorProps = {
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  value: RuntimeRef | undefined
  onChange: (ref: RuntimeRef | undefined) => void
}

const isRuntimeKind = (kind: EntityTypeKind) => getKnownKindKey(kind) === 'Runtime'

const formatDate = (ms: number): string => new Date(ms).toLocaleString()

const RuntimeSelector = ({ entityTypes, value, onChange }: RuntimeSelectorProps) => {
  const hasRuntimeTypes = useMemo(
    () => [...entityTypes.values()].some((type) => isRuntimeKind(type.kind)),
    [entityTypes]
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingRef, setPendingRef] = useState<RuntimeRef | undefined>(value)
  const pendingEntityId = pendingRef ? entityIdFromString(pendingRef.entityId) : undefined
  const { entity: selectedEntity, loading: entityLoading } = useEntityById(
    value ? entityIdFromString(value.entityId) : undefined
  )
  // Fall back to the cross-org registry when the runtime isn't visible in this organization.
  const showRegistryFallback = !!value && !entityLoading && selectedEntity === null
  const { detail: registryDetail, isLoading: registryLoading } = useRegistryAsset(
    showRegistryFallback ? value!.entityId : undefined
  )

  const { entity: pendingLocalEntity, loading: pendingLocalLoading } = useEntityById(pendingEntityId)
  const pendingInRegistry = !!pendingEntityId && !pendingLocalLoading && pendingLocalEntity === null
  const { detail: pendingRegistryDetail, isLoading: pendingRegistryLoading } = useRegistryAsset(
    pendingInRegistry ? pendingRef!.entityId : undefined
  )
  const resolvingPendingSource =
    !!pendingEntityId && (pendingLocalLoading || (pendingInRegistry && pendingRegistryLoading))

  const localVersionsQuery = useQuery({
    queryKey: ['versions', pendingRef?.entityId],
    queryFn: () => listVersions({ id: pendingEntityId! }),
    enabled: !!pendingEntityId && !!pendingLocalEntity,
  })
  const versions = pendingInRegistry ? pendingRegistryDetail?.versions ?? [] : localVersionsQuery.data ?? []

  const runtimeLabel = selectedEntity?.primaryLabelText ?? registryDetail?.name
  const runtimeTypeName = selectedEntity
    ? entityTypes.get(selectedEntity.entity.typeId)?.name
    : registryDetail
      ? 'cross-org'
      : undefined
  const versionSuffix = value?.versionId ? ` @ ${value.versionId.slice(0, 8)}…` : ''
  const isLoading = entityLoading || (showRegistryFallback && registryLoading)

  const versionPin = pendingEntityId && versions.length > 0 && (
    <TextField
      select
      label="Pin to version"
      value={pendingRef?.versionId ?? ''}
      onChange={(e) =>
        setPendingRef(pendingRef ? { ...pendingRef, versionId: e.target.value || undefined } : pendingRef)
      }
      fullWidth
    >
      <MenuItem value="">Latest (unpinned)</MenuItem>
      {versions.map((record) => (
        <MenuItem key={record.versionId} value={record.versionId}>
          {record.versionId.slice(0, 8)}… · {formatDate(record.createdAt)}
        </MenuItem>
      ))}
    </TextField>
  )

  return (
    <>
      <Link
        component="button"
        variant="body2"
        underline="always"
        color="primary"
        onClick={() => {
          setPendingRef(value)
          setDialogOpen(true)
        }}
      >
        {isLoading ? (
          <CircularProgress size={14} />
        ) : (
          `Runtime: ${
            runtimeLabel ? `${runtimeLabel}${runtimeTypeName ? ` (${runtimeTypeName})` : ''}${versionSuffix}` : 'None'
          }`
        )}
      </Link>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Runtime</DialogTitle>
        <DialogContent sx={{ overflow: 'visible', paddingTop: '16px !important' }}>
          <EntityPickerWithRegistry
            value={pendingEntityId}
            onChange={(id: EntityId | undefined) =>
              setPendingRef((prev) => {
                if (!id) return undefined
                const idString = id.toString()
                return { entityId: idString, versionId: prev?.entityId === idString ? prev.versionId : undefined }
              })
            }
            kindFilter={isRuntimeKind}
            registryKind={EntityTypeKind.Companion.Runtime.toString()}
            selectedVersionId={pendingRef?.versionId}
            onRegistryVersionSelect={(versionId) => setPendingRef((prev) => (prev ? { ...prev, versionId } : prev))}
            localPickerExtras={versionPin}
            hasLocalTypes={hasRuntimeTypes}
            valueSource={pendingInRegistry ? 'registry' : pendingLocalEntity ? 'org' : undefined}
            resolvingValueSource={resolvingPendingSource}
            typeLabel="Runtime type"
            entityLabel="Runtime"
            noOptionsText="No runtimes"
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => {
              onChange(undefined)
              setDialogOpen(false)
            }}
            sx={{ mr: 'auto' }}
          >
            Clear
          </Button>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!pendingRef}
            onClick={() => {
              onChange(pendingRef)
              setDialogOpen(false)
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RuntimeSelector
